export interface HealthCheckAppointmentResponse {
  status: number,
  data: {
    id: number,
    scheduledAt: Date,
    consultedAt: Date,
    createdAt: Date,
    reportedAt: Date,
    reExamination: boolean,
    reExaminationAt: Date;
  },
  statusText: string
}

export interface HealthCheckData {
  id: number,
  consultedAt?: string,
  createdAt?: string,
  updatedAt?: string,
  reExamination?: boolean,
  scheduledAt?: string,
}

export interface HealthCheckDataResponse {
  status: number,
  data: {
    result: {
      id: number,
      consultedAt: string,
      createdAt: string,
      updatedAt: string,
      reportedAt: string,
      reExamination: boolean,
    }[]
  },
  statusText: string
}

export interface UpdateHealthCheckDataResponse {
  id: number,
  consultedAt: string,
  createdAt: string,
  updatedAt: string,
  reportedAt: string,
  reExamination: boolean,
}

export interface LifeStyleDateResponse {
  status: number,
  data: {
    result: {
      id: number,
      startAt: string,
      createdAt: string,
    }[]
  },
  statusText: string
}

export interface LifeStyleDiseaseResponse {
  status: number,
  id: number,
  statusText: string
}

export interface HealthCheckApi {
  /** 健診データを取得 */
  getLatestHealthCheckData: () => Promise<HealthCheckDataResponse>;

  /** ヘルスチェックアポイント登録 */
  registerHealthCheckAppointment: (id: string, scheduledAt: string) => Promise<HealthCheckAppointmentResponse>;

  /** ヘルスチェックアポイント情報取得 */
  getHealthCheckAppointment: () => Promise<HealthCheckAppointmentResponse>;

  /** 指定IDの健診結果データ取得（年次の予約可能期間付き） */
  getHealthCheckById: (id: string | number) => Promise<any>;

  postHealthCheckDate: (id: string | number) => Promise<HealthCheckDataResponse>;

  updateHealthCheckData: (body: HealthCheckData) => Promise<UpdateHealthCheckDataResponse>;

  postLifeStyleStartDate: () => Promise<LifeStyleDateResponse>;

  getLifeStyleStartDate: () => Promise<LifeStyleDateResponse>;

  postMentalStartDate: () => Promise<LifeStyleDateResponse>;

  getMentalStartDate: () => Promise<LifeStyleDateResponse>;

  /** 生活習慣病プロジェクト作成 */
  createLifestyleDiseaseProject: () => Promise<LifeStyleDiseaseResponse>;

  /** 受診確認 */
  confirmConsulted: (id: number) => Promise<UpdateHealthCheckDataResponse>;

  /** 結果受取確認 */
  confirmReportReceived: (id: number) => Promise<UpdateHealthCheckDataResponse>;

  /** 再検査フラグ更新 */
  updateReExamination: (id: number, hasReExamination: boolean) => Promise<UpdateHealthCheckDataResponse>;
}

export const useHealthCheckApi = (fetch, axios): HealthCheckApi => {
  const getLatestHealthCheckData = async (): Promise<HealthCheckDataResponse> => {
    const response = await fetch('/company-user-health-checks');
    return response;
  };

  const registerHealthCheckAppointment = (id: string, scheduledAt: string): Promise<HealthCheckAppointmentResponse> => {
    return fetch(`/company-user-health-checks/${id}`, { scheduledAt });
  };

  const getHealthCheckAppointment = (): Promise<HealthCheckAppointmentResponse> => {
    return fetch('/company-user-health-checks/me');
  };

  const getHealthCheckById = async (id: string | number): Promise<any> => {
    const response = await axios.get(`/company-user-health-checks/${id}`);
    return response.data;
  };

  const postHealthCheckDate = async (id: string | number): Promise<HealthCheckDataResponse> => {
    const response = await axios.post(`/company-user-health-checks/${id}`, {
      consultedAt: new Date().toISOString(),
    });
    return response.data;
  };

  const updateHealthCheckData = async (body: HealthCheckData): Promise<UpdateHealthCheckDataResponse> => {
    const response = await axios.patch(`/company-user-health-checks/${body.id}`, body);
    return response.data;
  };

  const postLifeStyleStartDate = async (): Promise<LifeStyleDateResponse> => {
    const response = await axios.post('/company-user-lifestyle-diseases', {
      startAt: new Date().toISOString(),
    });
    return response.data;
  };

  const getLifeStyleStartDate = (): Promise<LifeStyleDateResponse> => {
    return fetch('/company-user-lifestyle-diseases/me');
  };

  const postMentalStartDate = async (): Promise<LifeStyleDateResponse> => {
    const response = await axios.post('/company-user-mental-healths', {
      startAt: new Date().toISOString(),
    });
    return response.data;
  };

  const getMentalStartDate = (): Promise<LifeStyleDateResponse> => {
    return fetch('/company-user-mental-healths/me');
  };

  const createLifestyleDiseaseProject = async (): Promise<LifeStyleDiseaseResponse> => {
    const response = await axios.post('/projects/lifestyle-disease');
    return response.data;
  };

  const confirmConsulted = async (id: number): Promise<UpdateHealthCheckDataResponse> => {
    const response = await axios.patch(`/company-user-health-checks/${id}`, {
      consultedAt: new Date().toISOString(),
    });
    return response.data;
  };

  const confirmReportReceived = async (id: number): Promise<UpdateHealthCheckDataResponse> => {
    const response = await axios.patch(`/company-user-health-checks/${id}`, {
      reportedAt: new Date().toISOString(),
    });
    return response.data;
  };

  const updateReExamination = async (id: number, hasReExamination: boolean): Promise<UpdateHealthCheckDataResponse> => {
    const response = await axios.post(`/company-user-health-checks/${id}`, {
      reExamination: hasReExamination,
    });
    return response.data;
  };

  return {
    getLatestHealthCheckData,
    registerHealthCheckAppointment,
    getHealthCheckAppointment,
    getHealthCheckById,
    postHealthCheckDate,
    postLifeStyleStartDate,
    getLifeStyleStartDate,
    postMentalStartDate,
    getMentalStartDate,
    createLifestyleDiseaseProject,
    updateHealthCheckData,
    confirmConsulted,
    confirmReportReceived,
    updateReExamination,
  };
};
